import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Grid,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const downloadBlob = (data, contentType, fileName) => {
  const blob = new Blob([data], { type: contentType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export default function DfcReport() {
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [paymentMode, setPaymentMode] = useState("");
  const [programs, setPrograms] = useState([]);
  const [branches, setBranches] = useState([]);
  const [programId, setProgramId] = useState("");
  const [branchId, setBranchId] = useState("");
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const getAuthHeaders = () => {
    const token = sessionStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  };

  const user = JSON.parse(sessionStorage.getItem("user") || "null");
  const clientId = user?.client_id;
  const collegeId = user?.college_id;

  useEffect(() => {
    if (!clientId || !collegeId) return;

    axios
      .get(`${API_BASE_URL}/api/programs/${clientId}/${collegeId}`, {
        headers: getAuthHeaders(),
      })
      .then((response) => setPrograms(response.data || []))
      .catch(() => {
        setPrograms([]);
        showSnackbar("Unable to load programs.", "error");
      });
  }, [clientId, collegeId]);

  useEffect(() => {
    if (!clientId || !collegeId) return;

    const params = programId ? { programId } : {};
    axios
      .get(`${API_BASE_URL}/api/branches/${clientId}/${collegeId}`, {
        headers: getAuthHeaders(),
        params,
      })
      .then((response) => setBranches(response.data?.data || []))
      .catch(() => {
        setBranches([]);
        showSnackbar("Unable to load branches.", "error");
      });
    setBranchId("");
  }, [clientId, collegeId, programId]);

  const handleGetDfcReport = async () => {
    if (!clientId || !collegeId) {
      showSnackbar("User college details not found.", "error");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        client_id: clientId,
        college_id: collegeId,
        from_date: fromDate || undefined,
        to_date: toDate || undefined,
        payment_mode: paymentMode || undefined,
        program_id: programId || undefined,
        branch_id: branchId || undefined,
      };
      const config = { headers: getAuthHeaders(), responseType: "blob" };
      const response = await axios.post(
        `${API_BASE_URL}/api/dfc-report/download-pdfs`,
        payload,
        config
      );

      if (branchId) {
        const selectedBranch = branches.find(
          (branch) => String(branch.branch_id) === String(branchId)
        );
        const branchName = selectedBranch?.branch_name || "Branch";
        downloadBlob(response.data, "application/pdf", `${branchName} DFC REPORT.pdf`);
        showSnackbar("DFC report PDF downloaded.");
      } else {
        downloadBlob(response.data, "application/zip", "DFC REPORTS.zip");
        showSnackbar("DFC reports ZIP downloaded.");
      }
    } catch (err) {
      showSnackbar(err.response?.data?.message || "Unable to download DFC report.", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Card sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          DFC - Daily Fee Collection
        </Typography>

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3} md={2}>
            <TextField
              label="Program"
              select
              value={programId}
              onChange={(event) => setProgramId(event.target.value)}
              InputLabelProps={{ shrink: true }}
              SelectProps={{ native: true, displayEmpty: true }}
              fullWidth
              size="small"
            >
              <option value="">All</option>
              {programs.map((program) => (
                <option key={program.program_id} value={program.program_id}>
                  {program.program_name}
                </option>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3} md={2}>
            <TextField
              label="Branch"
              select
              value={branchId}
              onChange={(event) => setBranchId(event.target.value)}
              InputLabelProps={{ shrink: true }}
              SelectProps={{ native: true, displayEmpty: true }}
              fullWidth
              size="small"
            >
              <option value="">All</option>
              {branches.map((branch) => (
                <option key={branch.branch_id} value={branch.branch_id}>
                  {branch.branch_name}
                </option>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3} md={2}>
            <TextField
              label="From Date"
              type="date"
              value={fromDate}
              onChange={(event) => setFromDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={3} md={2}>
            <TextField
              label="To Date"
              type="date"
              value={toDate}
              onChange={(event) => setToDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={3} md={2}>
            <TextField
              label="Payment Mode"
              select
              value={paymentMode}
              onChange={(event) => setPaymentMode(event.target.value)}
              InputLabelProps={{ shrink: true }}
              SelectProps={{ native: true, displayEmpty: true }}
              fullWidth
              size="small"
            >
              <option value="">All</option>
              <option value="CASH">CASH</option>
              <option value="ONLINE">ONLINE</option>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3} md={2.5}>
            <Button
              variant="contained"
              onClick={handleGetDfcReport}
              disabled={loading}
              sx={{ color: "#fff", minHeight: 40 }}
            >
              {loading ? <CircularProgress size={22} color="inherit" /> : "Get DFC Report"}
            </Button>
          </Grid>
        </Grid>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
